import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../components/auth/AuthProvider'
import { userService, User, InviteUserData, UpdateRolesData } from '../../services/userService'
import { staleTimeConfig } from '../../config/staleTimeConfig'

// Query keys
export const userKeys = {
  all: ['users'] as const,
  current: ['users', 'current'] as const,
}

// Get all users (directly from API - no transformation needed)
export function useUsers() {
  const { isLoaded, isSignedIn } = useAuth()

  return useQuery({
    queryKey: userKeys.all,
    queryFn: () => userService.getUsers(),
    enabled: isLoaded && isSignedIn,
    staleTime: staleTimeConfig.NORMAL,
  })
}

// Get current user
export function useCurrentUser() {
  return useQuery({
    queryKey: userKeys.current,
    queryFn: () => userService.getCurrentUser(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Invite user
export function useInviteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: InviteUserData) => userService.inviteUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all })
    }
  })
}

// Update user roles (new simplified endpoint)
export function useUpdateUserRoles() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateRolesData }) =>
      userService.updateUserRoles(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all })
    }
  })
}

// Deactivate user with optimistic update
export function useDeactivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => userService.deactivateUser(userId),

    onMutate: async (userId: string) => {
      await queryClient.cancelQueries({ queryKey: userKeys.all })
      const previousUsers = queryClient.getQueryData<User[]>(userKeys.all)

      queryClient.setQueryData<User[]>(userKeys.all, (old) =>
        old?.map(user =>
          user.id === userId
            ? { ...user, status: 'inactive' as const }
            : user
        )
      )

      return { previousUsers }
    },

    onError: (_err, _userId, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(userKeys.all, context.previousUsers)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all })
    }
  })
}

// Resend invitation to a user who hasn't activated yet
export function useResendInvitation() {
  return useMutation({
    mutationFn: (userId: string) => userService.resendInvitation(userId),
  })
}

// Activate user with optimistic update
export function useActivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => userService.activateUser(userId),

    onMutate: async (userId: string) => {
      await queryClient.cancelQueries({ queryKey: userKeys.all })
      const previousUsers = queryClient.getQueryData<User[]>(userKeys.all)

      queryClient.setQueryData<User[]>(userKeys.all, (old) =>
        old?.map(user =>
          user.id === userId
            ? { ...user, status: 'active' as const }
            : user
        )
      )

      return { previousUsers }
    },

    onError: (_err, _userId, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(userKeys.all, context.previousUsers)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all })
    }
  })
}
